import React, { useState, useEffect, useRef } from 'react'
import * as ReactDOM from 'react-dom'
import ErrorBoundary from '../ErrorBoundary'
import PropTypes from 'prop-types'
import * as wjCore from '@mescius/wijmo'
import * as wjMultiRow from '@mescius/wijmo.react.grid.multirow'
import { KeyAction, GroupRow } from '@mescius/wijmo.grid'
import '@mescius/wijmo.cultures/wijmo.culture.ja'
import customContextMenu from '../../wijmo/customContextMenu'
import editableFlexGridInitialize from '../../wijmo/editableFlexGridInitialize'
import colElements from '../../wijmo/columnElements'
import cellTemplateWithTitle from '../../wijmo/cellTemplateWithTitle'

import { makeStyles } from '@material-ui/core/styles'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@material-ui/core'

import moment from 'moment'

import renderContractTypeBadge from '../element/ContractTypeBadge'
import renderPeriodBadge from '../element/PeriodBadge'
import BootstrapAlert from '../element/BootstrapAlert'
import { multiplyDecimals } from '../../utility'

// MaterialUIのスタイル定義
const useStyles = makeStyles({
  tableContainer: {
    display: 'inline-block',
    maxWidth: 500,
    minWidth: 400
  },
  tableHead: {
    backgroundColor: 'silver'
  },
  tableFoot: {
    backgroundColor: 'gold'
  },
  cellEmphasized: {
    fontWeight: 'bold'
  }
})

function RoyalityTable (props) {
  const styles = useStyles()

  return (
    <TableContainer className={styles.tableContainer} component={Paper}>
      <Table size="small" aria-label="請求額">
        <TableHead className={styles.tableHead}>
          <TableRow>
            <TableCell><b>請求額</b></TableCell>
            <TableCell align="right"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.isXr
            ? (
            <TableRow className={styles.tableFoot}>
              <TableCell component="th" scope="row">
                使用料合計（請求額）
              </TableCell>
              <TableCell align="right">￥{props.sumAddRoyality.toLocaleString()}</TableCell>
            </TableRow>
              )
            : (
            <>
              <TableRow className={styles.tableFoot}>
                <TableCell component="th" scope="row">
                  追加分使用料額（請求額）
                </TableCell>
                <TableCell align="right">￥{props.sumAddRoyality.toLocaleString()}</TableCell>
              </TableRow>
            </>
              )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

RoyalityTable.propTypes = {
  sumAddRoyality: PropTypes.number,
  isXr: PropTypes.bool
}

function GrossSpentRoyalityTable (props) {
  const styles = useStyles()
  const { valueMap } = props

  return (
    <TableContainer className={styles.tableContainer} component={Paper}>
      <Table size="small" aria-label="グロス消化金額">
        <TableHead className={styles.tableHead}>
          <TableRow>
            <TableCell><b>グロス契約残額</b></TableCell>
            <TableCell align="right">消化金額</TableCell>
            <TableCell align="right">製造後残額</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from(valueMap).map(([contract, value]) => (
            <TableRow key={contract.id}>
              <TableCell component="th" scope="row">
                契約ID: {contract.coop_con_id ?? contract.id}
              </TableCell>
              <TableCell align="right">{
                contract.recouped_at ? '' : `￥${(value).toLocaleString()}`
              }</TableCell>
              <TableCell align="right">{
                contract.recouped_at ? 'リクープ済' : `￥${(contract.royality - contract.spent_royality - value).toLocaleString()}`
              }</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

GrossSpentRoyalityTable.propTypes = {
  valueMap: PropTypes.object
}

function getLayoutDefs (mode, isXr, isShopro) {
  const isReadOnly = (mode !== 'createTrade' && mode !== 'editTrade')
  const hideInputs = (mode === 'view')
  const isConfirm = (mode === 'confirm')
  const ldTwoLines = [
    {
      colspan: 3,
      cells: [
        { width: 0, maxWidth: 0, rowspan: 2 },
        { ...colElements.id, rowspan: 2 },
        { ...colElements.name, rowspan: 2, width: 250 }
      ]
    },
    {
      colspan: 3,
      cells: [
        colElements.price,
        colElements.rate_decimal,
        colElements.amount,
        { ...colElements.royality, colspan: 2 },
        colElements.royality_total
      ]
    },
    isConfirm
      ? {
          colspan: 1,
          cells: [
            colElements.used_amount,
            colElements.remain_amount
          ]
        }
      : {
          colspan: 3,
          cells: [
            colElements.used_amount,
            { ...colElements.unapproved_trade, rowspan: 2 },
            { ...colElements.draft, rowspan: 2 },
            colElements.remain_amount
          ]
        },
    hideInputs
      ? null
      : {
          colspan: 1,
          cells: [
            { ...colElements.use_amount, isReadOnly: isReadOnly, cssClassAll: hideInputs ? 'wj-style-green' : 'wj-style-green-input' },
            { ...colElements.stamp_amount }
          ]
        },
    isXr
      ? {
          colspan: 1,
          cells: [
            { ...colElements.expect_add_amount, header: '使用数量', cellTemplate: (ctx) => cellTemplateWithTitle('累計使用申請数', ctx.text) },
            { ...colElements.add_royality, header: '使用料', aggregate: 'Sum', cellTemplate: (ctx) => cellTemplateWithTitle('累計使用料額', ctx.text) }
          ]
        }
      : {
          colspan: 2,
          cells: [
            colElements.expect_use_amount,
            colElements.expect_add_amount,
            { ...colElements.spent_royality, aggregate: 'Sum' },
            { ...colElements.add_royality, aggregate: 'Sum' }
          ]
        },
    {
      colspan: 2,
      cells: [
        { ...colElements.stamp_notice },
        { ...colElements.stamp_ratio },
        isShopro ? { ...colElements.stamp_name, colspan: 2 } : { ...colElements.stamp_name, binding: '', header: ' ', colspan: 2 }
      ]
    },
    {
      colspan: isReadOnly ? 1 : 2,
      cells: [
        { ...colElements.comment, rowspan: 2, isReadOnly: isReadOnly, cssClassAll: hideInputs ? 'wj-style-green' : 'wj-style-green-input' },
        isReadOnly ? null : { ...colElements.comment_length }
      ].filter((col) => col)
    },
    {
      colspan: 2,
      cells: [
        {
          binding: ' ',
          header: ' ',
          rowspan: 2,
          align: 'center',
          width: 90,
          isReadOnly: true,
          cssClassAll: 'wj-style-green',
          cellTemplate: (ctx) => {
            return ctx.item.edit_url
              ? `<a href="${ctx.item.edit_url}" class="btn ol-theme-success py-0" title="グロス契約で一度も製造申請が完了していないアイテムは、登録情報を修正できます。">` +
               '修正' +
               '</a>'
              : ''
          },
          allowSorting: false,
          isRequired: false
        },
        {
          binding: ' ',
          header: ' ',
          rowspan: 2,
          align: 'center',
          width: 90,
          isReadOnly: true,
          cssClassAll: 'wj-style-green',
          cellTemplate: (ctx) => {
            return ctx.item.delete_url
              ? `<button type="button" name="${ctx.item.delete_url}" class="btn ol-theme-primary py-0 ig-delete" title="グロス契約で一度も製造申請していないアイテムは削除することができます。">` +
               '削除' +
               '</button>'
              : ''
          },
          allowSorting: false,
          isRequired: false
        }
      ]
    }
  ].filter((col) => col)
  return {
    def: ldTwoLines,
    frozenColumns: 3,
    isUseAmount: (r, c) => r % 2 === 0 && c === 9,
    stampAmountAxis: (r) => ({ row: r + 1, col: 9 })
  }
}

//
export default function ItemsGrid (props) {
  const isXr = props.contracts.length > 0 && props.contracts[0].xr_contract_id !== null
  const [itemsCV, setItemsCV] = useState(null)
  const [deleteUrl, setDeleteUrl] = useState(0)
  const [layoutDef] = useState(getLayoutDefs(props.mode, isXr, props.isShopro))
  const [sumAddRoyality, setSumAddRoyality] = useState(0)
  const [grossSpentRoyality, setGrossSpentRoyality] = useState(new Map())
  const [jsonElem] = useState(props.itemsJsonId ? $('#' + props.itemsJsonId) : null)
  const royalityOthersMap = useRef(new Map(props.royalityOthers.map((c) => [c.id, c])))
  const itemsDiffMap = useRef(new Map())

  const editMode = props.mode === 'createTrade' || props.mode === 'editTrade'
  const diffExist = props.itemsDiff.add.length > 0 || props.itemsDiff.change.length > 0 || props.itemsDiff.delete.length > 0

  // インポート/エクスポート時のテーブルを定義
  const tableFormat = [
    { field: 'coop_con_id', text: '契約ID', type: 'string', isEditable: false },
    { field: 'serial_no', text: 'No', type: 'string', isEditable: false },
    { field: 'name', text: '商品名', type: 'string', isEditable: false },
    { field: 'price', text: '税抜小売価格', type: 'number', isEditable: false },
    { field: 'rate_decimal', text: '料率', type: 'number', isEditable: false },
    { field: 'royality', text: '使用料単価', type: 'number', isEditable: false },
    { field: 'amount', text: '数量', type: 'number', isEditable: false },
    { field: 'royality_total', text: '使用料', type: 'number', isEditable: false },
    { field: 'used_amount', text: '累計製造数', type: 'number', isEditable: false },
    { field: 'remain_amount', text: '残数量', type: 'number', isEditable: false },
    { field: 'use_amount', text: '製造数量', type: 'number', isEditable: false },
    { field: 'stamp_amount', text: '証紙枚数', type: 'number', isEditable: false },
    { field: 'stamp_notice', text: '証紙特記', type: 'string', isEditable: false },
    { field: 'ratio_stamp', text: '掛率証紙', type: 'number', isEditable: false },
    { field: 'ratio_item', text: '掛率数量', type: 'number', isEditable: false },
    { field: 'comment', text: '備考', type: 'string', isEditable: true }
  ]

  function sortByProperty (cv, property, direction) {
    const descriptions = cv.sortDescriptions
    descriptions.clear()
    descriptions.push(new wjCore.SortDescription('contract_with_no_item', false)) // アイテム未登録の契約を頭に表示
    descriptions.push(new wjCore.SortDescription(property, direction === 'asc'))
    descriptions.push(new wjCore.SortDescription('contract_id', true))
  }

  function calcExpectUseAmount (item) {
    return item.contract.contract_type === 2 && item.gross_info
      ? (props.mode === 'confirm' ? item.gross_info.use_amount : editMode ? item.gross_info.tmp_use_amount : item.gross_info.past_use_amount)
      : Math.min(item.remain_amount, item.use_amount)
  }

  function calcExpectAddAmount (item) {
    return item.contract.contract_type === 2 && item.gross_info
      ? (props.mode === 'confirm' ? item.gross_info.add_amount : editMode ? item.gross_info.tmp_add_amount : item.gross_info.past_add_amount)
      : Math.max(item.use_amount - item.remain_amount, 0)
  }

  function calcGrossAmount (contractId, cv) {
    const items = cv.items.filter((item) => item.contract_id === contractId)
    if (items.length > 0) {
      const contract = items[0].contract
      if (contract.xr_contract_id) {
        // クロスリクープ契約
        items.forEach((item) => {
          // XRはすべて追加で登録
          item.gross_info.tmp_use_amount = 0
          item.gross_info.tmp_add_amount = item.use_amount
        })
      } else {
        // グロス契約
        let remainRoyality = contract.royality - contract.spent_royality - (royalityOthersMap.current.get(contract.id)?.use_royality ?? 0)
        let isRecouped = contract.recouped_at !== null
        items.forEach((item) => {
          const availableAmount = item.royality > 0 ? Math.floor(remainRoyality / item.royality) : 0
          if (isRecouped) {
            // リクープ済：すべて追加
            item.gross_info.tmp_use_amount = 0
            item.gross_info.tmp_add_amount = item.use_amount
          } else {
            // リクープ前：利用可能な個数より多ければ一部追加（これ以降はリクープ済）、そうでなければすべて消化
            if (item.use_amount > availableAmount) {
              item.gross_info.tmp_use_amount = availableAmount
              item.gross_info.tmp_add_amount = item.use_amount - availableAmount
              isRecouped = true
            } else {
              item.gross_info.tmp_use_amount = item.use_amount
              item.gross_info.tmp_add_amount = 0
            }
          }
          remainRoyality -= Math.round(multiplyDecimals(item.royality, item.gross_info.tmp_use_amount, 2, 0))
        })
      }
    }
  }

  useEffect(() => {
    if (!itemsCV) {
      const items = props.items
      const contractIdFromItems = new Set(items.map((item) => item.contract_id))
      const oldItems = new Map(props.oldItemsJson.map((item) => [item.id, item]))
      props.contracts.forEach((contract) => {
        if (contract.character.is_default) {
          contract.character.title = '★' + contract.character_name_free
        }
        contract.toString = () => {
          if (!contract.xr_contract_id) {
            let contractStr = `ID: ${contract.coop_con_id || '-'} / ${contract.character.title}`
            if (contract.contract_type !== 1) {
              if (contract.recouped_at) {
                contractStr += ` / リクープ済（${contract.recouped_at}）`
              } else {
                contractStr += ` / 残￥${(contract.royality - contract.spent_royality - (royalityOthersMap.current.get(contract.id)?.use_royality ?? 0)).toLocaleString()}`
              }
            }
            const contractTypeBadge = `<span class="contract-type-badge" data-contract-type="${contract.contract_type}" data-is-xr="false"></span>`
            const periodBadge = `<span class="period-badge" data-period-from="${contract.period_from}" data-period-to="${contract.period_to}"></span>`
            const deadlineStr = contract.application_deadline ? `<b class="text-danger">製造申請期限：${contract.application_deadline}</b>` : ''
            return `<span>${contractTypeBadge}${periodBadge}${deadlineStr}<br/><a href="${contract.contract_url || '#'}" target="_blank">${contractStr}</a></span>`
          } else {
            // クロスリクープの個別契約
            const note = contract.xr_note ? `（${contract.xr_note}）` : ''
            const contractStr = `ID: ${contract.coop_con_id || '-'} / ${contract.character.title}${note} / 目標額 ￥${contract.royality.toLocaleString()} / 使用済 ￥${contract.spent_royality.toLocaleString()}`
            const contractTypeBadge = `<span class="contract-type-badge" data-contract-type="${contract.contract_type}" data-is-xr="true"></span>`
            const periodBadge = `<span class="period-badge" data-period-from="${contract.period_from}" data-period-to="${contract.period_to}"></span>`
            const deadlineStr = contract.application_deadline ? `<b class="text-danger">製造申請期限：${contract.application_deadline}</b>` : ''
            return `<span>${contractTypeBadge}${periodBadge}${deadlineStr}<br/><a href="${contract.contract_url || '#'}" target="_blank">${contractStr}</a></span>`
          }
        }
        // グロス契約のアイテム登録へのリンク行を追加
        if (contract.contract_type === 2 && contract.register_item_url) {
          const url = new URL(contract.register_item_url)
          url.searchParams.append('prev', location.href)
          items.push({
            id: 0,
            contract_id: contract.id,
            name: null,
            price: null,
            rate: null,
            ratio_stamp: null,
            ratio_item: null,
            used_amount: null,
            gross_info: {
              use_amount: null,
              add_amount: null,
              past_use_amount: null,
              past_add_amount: null
            },
            character_id: contract.character_id,
            applied_at: contract.xr_parent?.applied_at ?? contract.applied_at, // XR契約の場合は親の申請日でソートする
            period_to: contract.period_to,
            register_url: url, // アイテム登録リンクを表示するための情報 表示処理はcustomContextMenu.js内
            contract_with_no_item: !contractIdFromItems.has(contract.id), // アイテム未登録の契約に対するアイテム登録リンクであることを判別するフラグ,
            exclude_from_export: true // この行はエクスポートしない
          })
        }
      })
      const contractById = new Map(props.contracts.map((contract) => [contract.id, contract]))
      const grossIdList = props.contracts.filter((contract) => contract.contract_type === 2).map((contract) => contract.id)
      const inputFields = 'id,use_amount,comment'.split(',')
      items.map((item, i) => {
        item.contract = contractById.get(item.contract_id)

        item.coop_con_id = item.contract.coop_con_id
        item.rate = 'rate' in item ? Number(item.rate) : null
        item.royality = 'royality' in item ? Number(item.royality) : null
        if ('spent_royality_prod' in item) {
          item.spent_royality_prod = Number(item.spent_royality_prod)
        }
        if ('add_royality_prod' in item) {
          item.add_royality_prod = Number(item.add_royality_prod)
        }

        if (item.contract.contract_type === 2) {
          item.amount = '-'
        }

        if (props.mode === 'editTrade') {
          item.use_amount = item.fixed_use_amount + item.fixed_add_amount
          delete item.fixed_use_amount
          delete item.fixed_add_amount
        } else {
          if ('fixed_use_amount' in item && 'fixed_add_amount' in item) {
            item.use_amount = item.fixed_use_amount + item.fixed_add_amount
          }
        }

        // デフォルト値（元の入力データなど）を格納
        const defaultValues = oldItems.has(item.id)
          ? oldItems.get(item.id)
          : {
              use_amount: 0,
              comment: item.comment
            }
        if (!item.use_amount || defaultValues.use_amount > 0) item.use_amount = defaultValues.use_amount
        item.comment = defaultValues.comment

        // ソート用のプロパティ設定
        item.character_name = item.contract.character_name_free || (item.contract.character ? item.contract.character.title : '')
        if (item.id !== 0) {
          item.contract_with_no_item = false
        }
        // 証紙が印刷or免除の場合、application_deadlineまでは申請可能
        const deadline = ['印刷', '免除'].includes(item.stamp_notice) && item.contract.application_deadline
          ? item.contract.application_deadline
          : item.contract.period_to

        // アイテムの編集可否を設定
        item.editable = !(
          item.id === 0 ||
          item.unapproved_trade_url ||
          moment(deadline).add(1, 'd').isBefore(moment())
        )
        // 表示用の証紙掛率を設定
        item.stamp_ratio = item.ratio_item ? `${item.ratio_stamp} / ${item.ratio_item}` : ''
        return 0
      })

      props.itemsDiff.change.forEach((diff) => {
        itemsDiffMap.current.set(diff.serial_no, diff.item)
      })
      props.itemsDiff.add.forEach((diff) => {
        itemsDiffMap.current.set(diff.serial_no, diff.item)
      })
      props.itemsDiff.delete.forEach((diff) => {
        itemsDiffMap.current.set(diff.serial_no, diff.item)
      })

      const getError = (item, propName) => {
        if (['price', 'royality'].includes(propName)) {
          // testRoyality: 見た目で（＝価格が四捨五入された値で）計算された使用料
          const testRoyality = Math.round(multiplyDecimals(Math.round(item.price), item.rate_decimal * 100, 0, 2)) / 100
          if (Math.abs(item.royality - testRoyality) > 0.001) {
            return `使用料単価が整数での計算値（￥${testRoyality.toFixed(2)}）と異なる可能性があります。ご注意ください。`
          }
        }
        return null
      }
      const _itemsCV = new wjCore.CollectionView(items, {
        calculatedFields: {
          rate_decimal: (obj) => 'rate' in obj ? obj.rate / 100 : null,
          royality_total: (obj) => obj.contract.contract_type === 2 ? '-' : obj.royality_prod,
          remain_amount: (obj) => obj.contract.contract_type === 2 ? '-' : Math.max(obj.amount - obj.used_amount, 0),
          expect_use_amount: (obj) => 'fixed_use_amount' in obj ? obj.fixed_use_amount : calcExpectUseAmount(obj),
          expect_remain_amount: (obj) => obj.contract.contract_type === 2 && obj.gross_info ? 0 : obj.remain_amount - obj.expect_use_amount,
          expect_add_amount: (obj) => 'fixed_add_amount' in obj ? obj.fixed_add_amount : calcExpectAddAmount(obj),
          spent_royality: (obj) => obj.contract.contract_type === 1 ? '-' : ((!editMode && 'use_royality_prod' in obj) ? obj.use_royality_prod : Math.round(multiplyDecimals(obj.royality, obj.expect_use_amount, 2, 0))),
          add_royality: (obj) => (!editMode && 'add_royality_prod' in obj) ? obj.add_royality_prod : Math.round(multiplyDecimals(obj.royality, obj.expect_add_amount, 2, 0)),
          stamp_amount: (obj) => Math.ceil(obj.expect_use_amount * obj.ratio_stamp / obj.ratio_item) + Math.ceil(obj.expect_add_amount * obj.ratio_stamp / obj.ratio_item),
          comment_length: (obj) => obj.comment?.length ?? 0
        },
        groupDescriptions: ['contract'],
        trackChanges: true,
        getError: getError
      })

      _itemsCV.collectionChanged.addHandler((cv, e) => {
        // 入力値をフィールドの型に変換
        if (e.item && e.item.use_amount) {
          e.item.use_amount = Math.floor(e.item.use_amount)
        }
        if (e.item && e.item.contract && e.item.contract.contract_type !== 1) {
          calcGrossAmount(e.item.contract_id, _itemsCV)
        }

        setSumAddRoyality(
          _itemsCV.getAggregate(wjCore.Aggregate.Sum, colElements.add_royality.binding) +
          props.royalityOthers.reduce((total, c) => total + c.add_royality, 0)
        )
        const grossSpentRoyalityMap = new Map(_itemsCV.groups
          .filter((group) => group.name.contract_type === 2 && !group.name.xr_contract_id)
          .map((group) => [
            group.name,
            group.getAggregate('Sum', 'spent_royality') + (royalityOthersMap.current.get(group.name.id)?.use_royality ?? 0)
          ])
        )
        setGrossSpentRoyality(grossSpentRoyalityMap)

        // アイテム情報のJSONを隠し要素に記録（フォーム送信時に参照する）
        jsonElem && jsonElem.val(JSON.stringify(_itemsCV.items.filter((item) => item.id !== 0), inputFields))
      })
      grossIdList.forEach((id) => {
        calcGrossAmount(id, _itemsCV)
      })
      _itemsCV.collectionChanged.raise(_itemsCV)

      setItemsCV(_itemsCV)

      const url = new URL(location)
      sortByProperty(
        _itemsCV,
        url.searchParams.get('order_by') ?? 'applied_at',
        url.searchParams.get('order_direction') ?? 'desc'
      )

      $('button[type="submit"]').each((i, el) => {
        $(el).on('click', (e) => { window.onbeforeunload = null })
      })

      window.onbeforeunload = (e) => {
        if (_itemsCV.itemsEdited.length > 0) {
          const message = 'ページを離れると現在の入力内容が失われます。内容を保持したい場合は「入力を保存」をクリックしてください。'
          e.returnValue = message
          return message
        } else {
          return null
        }
      }
    }
  }, [itemsCV])

  useEffect(() => {
    if (deleteUrl !== 0) {
      $('#deleteModal').modal()
    }
  }, [deleteUrl])

  const gridInit = (grid) => {
    grid.formatItem.addHandler((s, e) => {
      if (e.panel === s.cells) {
        const row = e.getRow()
        const item = row.dataItem
        const column = grid.getBindingColumn(e.panel, e.row, e.col)

        // 契約方式・契約期間のバッジを描画
        if (e.cell.classList.contains('wj-group')) {
          renderContractTypeBadge(e.cell)
          renderPeriodBadge(e.cell)
        }
        // アイテム登録へのリンク行はname列以外何も表示しない
        if (item.id === 0) {
          if (column.binding !== 'name') {
            e.cell.textContent = ''
          }
        }
        // 編集不可の行のスタイル変更
        if (e.cell.classList.contains('wj-style-green-input')) {
          if (!item.editable) {
            wjCore.removeClass(e.cell, 'wj-style-green-input')
            wjCore.addClass(e.cell, 'wj-style-green')
          }
        }
        // アイテム削除ボタンのクリック時処理を追加
        $(e.cell).find('.ig-delete').each((i, el) => {
          $(el).on('click', (e) => {
            setDeleteUrl(el.name)
          })
        })
        // 申請修正時、変更箇所をマーキング
        const diff = itemsDiffMap.current.get(item.serial_no)
        if (diff) {
          let hasDiff = false
          if (column.binding === 'use_amount') {
            hasDiff = ('use_amount' in diff || 'add_amount' in diff)
          } else if (column.binding === 'comment') {
            hasDiff = 'comment' in diff
          } else if (column.binding === 'rate_decimal') {
            hasDiff = 'rate' in diff
          } else if (column.binding === 'spent_royality') {
            hasDiff = 'spent_royality_prod' in diff
          } else if (column.binding === 'add_royality') {
            hasDiff = 'add_royality_prod' in diff
          } else if (column.binding === 'stamp_ratio' || column.binding === 'stamp_amount') {
            hasDiff = 'ratio_stamp' in diff || 'ratio_item' in diff
          } else {
            hasDiff = column.binding in diff
          }

          if (hasDiff) {
            wjCore.addClass(e.cell, 'wj-cell-alert')
          }
        }
        // 消化金額の集計を制御（アイテム個別のときは表示しない）
        if (column.binding === 'spent_royality' && row instanceof GroupRow) {
          const contract = item.name
          if (contract.contract_type === 1) {
            e.cell.textContent = '-'
          }
        }
      }
    })
    grid.beginningEdit.addHandler((s, e) => {
      // 未承認製造が存在するアイテムは編集不可
      const item = e.getRow().dataItem
      if (!item.editable) {
        e.cancel = true
      }
    })

    grid.tableFormat = tableFormat

    customContextMenu(grid)
    editableFlexGridInitialize(grid)
  }

  return (
    <>
      <div className="text-center w-100">
        <div className="d-inline-block mw-100">
          {diffExist && (
            <div className="text-left">
              <BootstrapAlert class="danger">
                <div>赤色で塗られた項目が変更箇所です。</div>
              </BootstrapAlert>
            </div>
          )}
          <wjMultiRow.MultiRow showMarquee={true} stickyHeaders={true} itemsSource={itemsCV} layoutDefinition={layoutDef.def}
            keyActionTab={KeyAction.CycleEditable} validateEdits={false}
            frozenColumns={layoutDef.frozenColumns} multiRowGroupHeaders={true} initialized={gridInit}
            groupHeaderFormat="<b>{value}</b>"
            style={{ maxHeight: '500px' }}
          >
          </wjMultiRow.MultiRow>
          {props.displayRoyalityTotal && (
            <div className="w-100 text-right my-2">
              <RoyalityTable
                sumAddRoyality={sumAddRoyality}
                isXr={isXr}
              />
              <div>
                <small>※暫定的な計算に基づく金額です。確認画面で正確な金額をお確かめください。</small>
              </div>
            </div>
          )}
          {props.displayRoyalityTotal && grossSpentRoyality.size > 0 && (
            <div className="w-100 text-right my-2">
              <GrossSpentRoyalityTable
                valueMap={grossSpentRoyality}
              />
            </div>
          )}
        </div>
      </div>
      <div className="modal fade" id="deleteModal" tabIndex="-1" role="dialog" aria-labelledby="deleteModalLabel" aria-hidden="true">
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="deleteModalLabel">アイテム削除確認</h5>
              <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <p>このアイテムは一度も使用申請されていないため、削除することができます。</p>
              <p>このアイテムを削除しますか？</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-dismiss="modal">キャンセル</button>
              <form action={deleteUrl} method="post">
                <input type="text" className="d-none" name="_token" value={$('meta[name="csrf-token"]').attr('content')} readOnly />
                <button type="submit" className="btn bg-theme-primary">削除</button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

ItemsGrid.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  contracts: PropTypes.arrayOf(PropTypes.object),
  mode: PropTypes.oneOf('view createTrade editTrade confirm'.split(' ')),
  itemsJsonId: PropTypes.string,
  oldItemsJson: PropTypes.arrayOf(PropTypes.object),
  displayRoyalityTotal: PropTypes.bool,
  royalityOthers: PropTypes.arrayOf(PropTypes.object),
  itemsDiff: PropTypes.object,
  isShopro: PropTypes.bool
}

ItemsGrid.defaultProps = {
  itemsJsonId: '',
  oldItemsJson: [],
  displayRoyalityTotal: true,
  itemsDiff: { add: [], change: [], delete: [] },
  royalityOthers: [],
  isShopro: false
}

$('#items-grid').each((i, el) => {
  const qel = $(el)
  const props = qel.data() // bladeでdata-xxxx形式で指定した値がpropsとして参照できる
  ReactDOM.render(
    <ErrorBoundary>
      <ItemsGrid {...props}/>
    </ErrorBoundary>,
    el
  )

  Object.keys(props).map((key) => { // ソースにdata-属性が残らないように削除
    qel.removeAttr('data-' + key.replace(/[A-Z]/g, (s) => '-' + s.charAt(0).toLowerCase()))
    return 0
  })
})
